import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Loader2,
  MapPin,
  Power,
  Clock,
  Navigation,
  Save,
} from "lucide-react";
import MapView from "@/components/MapView";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { useAuth } from "@/auth/AuthContext";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function emptyRow(day) {
  return {
    id: null,
    day_of_week: day,
    open_time: "",
    close_time: "",
    address: "",
    lat: null,
    lng: null,
    is_closed: false,
  };
}

export default function Location() {
  const { vendor, refresh } = useAuth();

  // Weekly schedule
  const [rows, setRows] = useState(() => DAYS.map(emptyRow));
  const [loadingHours, setLoadingHours] = useState(true);
  const [savingDay, setSavingDay] = useState(null);

  // Go Live
  const [currentLoc, setCurrentLoc] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);
  const [liveForm, setLiveForm] = useState({
    name: "",
    address: "",
    lat: null,
    lng: null,
  });
  const [gpsBusy, setGpsBusy] = useState(false);

  const loadHours = useCallback(async () => {
    try {
      const { data } = await api.get("/operating-hours");
      const byDay = {};
      (data || []).forEach((h) => {
        byDay[h.day_of_week] = h;
      });
      setRows(
        DAYS.map((d) => {
          const h = byDay[d];
          if (!h) return emptyRow(d);
          return {
            id: h.id,
            day_of_week: d,
            open_time: h.open_time || "",
            close_time: h.close_time || "",
            address: h.address || "",
            lat: h.lat ?? null,
            lng: h.lng ?? null,
            is_closed: !!h.is_closed,
          };
        })
      );
    } catch (err) {
      console.error("[Location] loadHours failed:", err?.message || err);
      toast.error("Failed to load schedule");
    } finally {
      setLoadingHours(false);
    }
  }, []);

  const loadCurrent = useCallback(async () => {
    try {
      const { data } = await api.get("/vendors/me/current-location");
      setCurrentLoc(data);
    } catch (err) {
      console.error("[Location] loadCurrent failed:", err?.message || err);
      setCurrentLoc(null);
    }
  }, []);

  useEffect(() => {
    loadHours();
    loadCurrent();
  }, [loadHours, loadCurrent]);

  const updateRow = (idx, patch) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const saveDay = async (idx) => {
    const row = rows[idx];
    setSavingDay(row.day_of_week);
    try {
      const payload = {
        day_of_week: row.day_of_week,
        open_time: row.is_closed ? null : row.open_time || null,
        close_time: row.is_closed ? null : row.close_time || null,
        address: row.address || null,
        lat: row.lat,
        lng: row.lng,
        is_closed: !!row.is_closed,
      };
      let updated;
      if (row.id) {
        const { data } = await api.put(`/operating-hours/${row.id}`, payload);
        updated = data;
      } else {
        const { data } = await api.post("/operating-hours", payload);
        updated = data;
      }
      updateRow(idx, { id: updated.id });
      toast.success(`${row.day_of_week} saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingDay(null);
    }
  };

  // Saved locations from weekly schedule that have coords — used as quick "Go live here"
  const savedLocations = useMemo(
    () =>
      rows.filter(
        (r) => r.lat != null && r.lng != null && (r.address || r.day_of_week)
      ),
    [rows]
  );

  const useGPS = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser");
      return;
    }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLiveForm((f) => ({
          ...f,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        }));
        setGpsBusy(false);
        toast.success("Got your location");
      },
      (err) => {
        setGpsBusy(false);
        toast.error(err.message || "Unable to get GPS location");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const pickSaved = (r) => {
    setLiveForm({
      name: r.day_of_week,
      address: r.address || "",
      lat: r.lat,
      lng: r.lng,
    });
  };

  const goLive = async () => {
    if (liveForm.lat == null || liveForm.lng == null) {
      return toast.error("Pick a location first");
    }
    setLiveBusy(true);
    try {
      await api.post("/vendors/me/go-live", {
        name: liveForm.name || null,
        address: liveForm.address || null,
        lat: Number(liveForm.lat),
        lng: Number(liveForm.lng),
      });
      toast.success("You're live!");
      await refresh();
      await loadCurrent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to go live");
    } finally {
      setLiveBusy(false);
    }
  };

  const goOffline = async () => {
    setLiveBusy(true);
    try {
      await api.post("/vendors/me/go-offline");
      toast.success("You're offline");
      await refresh();
      await loadCurrent();
    } catch (_e) {
      toast.error("Failed to go offline");
    } finally {
      setLiveBusy(false);
    }
  };

  const isLive = !!vendor?.is_live && !!currentLoc;

  const mapCenter = useMemo(() => {
    if (liveForm.lat != null && liveForm.lng != null)
      return [Number(liveForm.lat), Number(liveForm.lng)];
    if (currentLoc?.lat != null && currentLoc?.lng != null)
      return [currentLoc.lat, currentLoc.lng];
    return [40.7128, -74.006];
  }, [liveForm.lat, liveForm.lng, currentLoc]);

  return (
    <div className="space-y-8 max-w-5xl" data-testid="location-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Location</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Plan your week and go live so customers can find you on the map.
        </p>
      </div>

      {/* Weekly schedule */}
      <section
        className="bg-white border border-slate-200 rounded-xl p-5"
        data-testid="weekly-schedule-section"
      >
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-[#E8650A]" />
          <h2 className="text-lg font-semibold">Weekly schedule</h2>
        </div>

        {loadingHours ? (
          <div className="text-slate-500 text-sm">Loading schedule…</div>
        ) : (
          <div className="space-y-3" data-testid="weekly-schedule-list">
            {rows.map((r, idx) => (
              <div
                key={r.day_of_week}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-end border-b border-slate-100 pb-3 last:border-b-0 last:pb-0"
                data-testid={`hours-row-${r.day_of_week}`}
              >
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-500">Day</Label>
                  <div className="font-semibold mt-1">{r.day_of_week}</div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-500">Open</Label>
                  <Input
                    type="time"
                    value={r.open_time}
                    disabled={r.is_closed}
                    onChange={(e) =>
                      updateRow(idx, { open_time: e.target.value })
                    }
                    data-testid={`hours-open-${r.day_of_week}`}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-500">Close</Label>
                  <Input
                    type="time"
                    value={r.close_time}
                    disabled={r.is_closed}
                    onChange={(e) =>
                      updateRow(idx, { close_time: e.target.value })
                    }
                    data-testid={`hours-close-${r.day_of_week}`}
                  />
                </div>
                <div className="md:col-span-4">
                  <Label className="text-xs text-slate-500">
                    Address / spot
                  </Label>
                  <Input
                    value={r.address}
                    placeholder="123 Main St or 'Lunch lot'"
                    onChange={(e) =>
                      updateRow(idx, { address: e.target.value })
                    }
                    data-testid={`hours-address-${r.day_of_week}`}
                  />
                </div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={r.is_closed}
                      onChange={(e) =>
                        updateRow(idx, { is_closed: e.target.checked })
                      }
                      data-testid={`hours-closed-${r.day_of_week}`}
                    />
                    Closed
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => saveDay(idx)}
                    disabled={savingDay === r.day_of_week}
                    data-testid={`hours-save-${r.day_of_week}`}
                  >
                    {savingDay === r.day_of_week ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Save size={14} />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Go Live section */}
      <section
        className="bg-white border border-slate-200 rounded-xl p-5"
        data-testid="go-live-section"
      >
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Power size={18} className={isLive ? "text-emerald-600" : "text-[#E8650A]"} />
            <h2 className="text-lg font-semibold">Go live</h2>
          </div>
          {isLive ? (
            <div className="flex items-center gap-2" data-testid="live-status-pill">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium">
                <span className="ff-live-dot-sm" />
                Live · {currentLoc?.name || currentLoc?.address || "current spot"}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={goOffline}
                disabled={liveBusy}
                data-testid="go-offline-btn"
              >
                {liveBusy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Power size={14} className="mr-1" />}
                Go offline
              </Button>
            </div>
          ) : (
            <span className="text-xs text-slate-500" data-testid="offline-pill">Currently offline</span>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-slate-600">Spot name (optional)</Label>
              <Input
                value={liveForm.name}
                onChange={(e) =>
                  setLiveForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Friday Lunch Lot"
                data-testid="live-name"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-600">Address</Label>
              <AddressAutocomplete
                value={liveForm.address}
                onChange={(v) =>
                  setLiveForm((f) => ({ ...f, address: v }))
                }
                onSelect={({ address, lat, lng }) =>
                  setLiveForm((f) => ({
                    ...f,
                    address,
                    lat,
                    lng,
                  }))
                }
                placeholder="Start typing an address…"
                testid="live-address-autocomplete"
              />
              {liveForm.lat != null && liveForm.lng != null && (
                <p className="text-xs text-slate-500 mt-1.5" data-testid="live-coords-label">
                  <MapPin size={11} className="inline mr-1 -mt-0.5" />
                  {Number(liveForm.lat).toFixed(5)}, {Number(liveForm.lng).toFixed(5)}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={useGPS}
                disabled={gpsBusy}
                data-testid="use-gps-btn"
              >
                {gpsBusy ? (
                  <Loader2 size={14} className="animate-spin mr-1" />
                ) : (
                  <Navigation size={14} className="mr-1" />
                )}
                Use device GPS
              </Button>

              <Button
                type="button"
                className="ff-btn-primary"
                onClick={goLive}
                disabled={liveBusy || liveForm.lat == null || liveForm.lng == null}
                data-testid="go-live-btn"
              >
                {liveBusy ? (
                  <Loader2 size={14} className="animate-spin mr-1" />
                ) : (
                  <Power size={14} className="mr-1" />
                )}
                {isLive ? "Switch location" : "Go live here"}
              </Button>
            </div>

            {savedLocations.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <div className="text-xs font-semibold text-slate-500 mb-2">
                  Saved spots from your schedule
                </div>
                <div className="flex flex-wrap gap-2" data-testid="saved-locations-list">
                  {savedLocations.map((r) => (
                    <button
                      key={r.day_of_week}
                      type="button"
                      onClick={() => pickSaved(r)}
                      className="inline-flex items-center gap-1.5 text-xs bg-slate-50 hover:bg-[#FDECE2] hover:text-[#E8650A] border border-slate-200 px-2.5 py-1.5 rounded-full transition"
                      data-testid={`saved-loc-${r.day_of_week}`}
                    >
                      <MapPin size={12} />
                      {r.day_of_week}
                      {r.address ? ` · ${r.address}` : ""}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div data-testid="go-live-map-wrap">
            <MapView
              center={mapCenter}
              zoom={liveForm.lat != null ? 14 : 4}
              markers={
                liveForm.lat != null && liveForm.lng != null
                  ? [
                      {
                        vendor_id: "preview",
                        lat: Number(liveForm.lat),
                        lng: Number(liveForm.lng),
                        is_live: isLive,
                        business_name: liveForm.name || vendor?.business_name || "Preview",
                        address: liveForm.address,
                      },
                    ]
                  : []
              }
              onMapClick={({ lat, lng }) =>
                setLiveForm((f) => ({ ...f, lat, lng }))
              }
              height={360}
              testid="go-live-map"
            />
            <p className="text-xs text-slate-500 mt-2">
              Tip: click anywhere on the map to drop a pin, or use GPS.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
